import { Collapse, Typography } from 'antd';
import { SoundOutlined } from '@ant-design/icons';
import React from 'react'
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function DynamicLayout(props) {
    const { data, speech } = props;
    const navigate = useNavigate();
    const { t } = useTranslation();

    const style = {
        fontWeight: data?.fontWeight === "bold" ? "bold" : "regular",
        fontStyle: data?.italicType === "italic" ? "italic" : "normal",
        textDecoration: data?.textDecoration,
        textAlign: data?.textAlign,

        marginBottom: data?.marginBottom ?? "0px",
        ...data?.marginTop ? { marginTop: data.marginTop } : {},
        ...data?.marginLeft ? { marginLeft: data.marginLeft } : {},
        ...data?.marginRight ? { marginRight: data.marginRight } : {},

        ...data?.width ? { width: data.width } : {},
        ...data?.height ? { height: data.height } : {},
    };

    const getContent = () => {
        return (
            <p style={style} className="flex items-start">
                <span className="flex-1">{data?.text}</span>
                {/* {renderInlineAudio(data?.text)} */}
            </p>
        );
    }

    const getLink = () => {
        const rawUrl = data?.linkUrl || data?.link || (data?.tag === 'link' ? data?.url : '');
        const { path: targetPath, isExternal } = resolveLink(
            rawUrl,
            data?.linkType,
            data?.type || data?.targetType,
            data?.targetId || data?.linkId
        );

        const handleClick = (e) => {
            if (!targetPath) return;
            if (isExternal) {
                // Handled natively by <a>
            } else {
                if (e) e.preventDefault();
                window.scrollTo(0, 0);
                navigate(targetPath);
            }
        };

        return (
            <Typography.Link
                style={style}
                href={targetPath || data?.link || data?.linkUrl}
                target={isExternal ? "_blank" : "_self"}
                rel={isExternal ? "noopener noreferrer" : undefined}
                onClick={handleClick}
            >
                {data?.text}
            </Typography.Link>
        );
    };

    const getButton = () => {
        const rawUrl = data?.linkUrl || data?.link || data?.url;
        const { path: targetPath, isExternal } = resolveLink(
            rawUrl,
            data?.linkType,
            data?.type || data?.targetType,
            data?.targetId || data?.linkId
        );

        const handleClick = (e) => {
            if (!targetPath) return;
            if (isExternal) {
                window.open(targetPath, '_blank', 'noopener,noreferrer');
            } else {
                if (e) e.preventDefault();
                window.scrollTo(0, 0);
                navigate(targetPath);
            }
        };

        return (
            <div style={{ textAlign: style?.textAlign || 'left', marginBottom: data?.marginBottom ?? "0px" }}>
                <button
                    type="button"
                    onClick={handleClick}
                    style={{
                        ...style,
                        backgroundColor: data?.backgroundColor || "#E50A47",
                        color: data?.color || "#FFFFFF",
                        border: "none",
                        padding: "8px 16px",
                        borderRadius: "4px",
                        cursor: (targetPath || rawUrl) ? "pointer" : "default"
                    }}
                >
                    {data?.text}
                </button>
            </div>
        );
    };

    const getBullet = () => {
        return <ul className='ul' style={{ paddingLeft: "35px" }}>
            <li className='li flex items-start' style={style}>
                <span className="flex-1">{data?.text}</span>
                {/* {renderInlineAudio(data?.text)} */}
            </li>
        </ul>
    }

    const getTitle = (level) => {
        return (
            <div className="flex items-center">
                <Typography.Title
                    className={`h${level} flex-1 m-0`}
                    style={{ ...style, marginBottom: 0 }}
                    level={level}
                >
                    {data?.text}
                </Typography.Title>
                {/* {renderInlineAudio(data?.text)} */}
            </div>
        );
    }
    
    const INTERNAL_PATHNAME_PREFIXES = [
        '/news',
        '/product',
        '/product-knowledge',
        '/technical',
        '/online-class',
        '/online-class-member',
        '/fashion',
        '/gallery',
        '/magazine',
        '/events',
        '/shareform'
    ];
    const INTERNAL_BARE_PREFIXES = [
        'news',
        'product',
        'product-knowledge',
        'technical',
        'online-class',
        'online-class-member',
        'fashion',
        'gallery',
        'magazine',
        'events',
        'shareform'
    ];
    const EXTERNAL_LINK_TYPE = 'external';
    const PRODUCT_BASE_PATH = '/product/';

    const resolveLink = (linkUrl, linkType, type, targetId) => {
        const raw = linkUrl || targetId || '';
        if (!raw && !targetId) return { path: '', isExternal: false };

        const trimmed = String(raw).trim();

        // Absolute HTTP/HTTPS URLs — check if they target an internal route first
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
            try {
                const { pathname, search, hash } = new URL(trimmed);
                const isInternalRoute = INTERNAL_PATHNAME_PREFIXES.some((prefix) =>
                    pathname.includes(prefix)
                );
                if (isInternalRoute) {
                    return { path: `${pathname}${search}${hash}`, isExternal: false };
                }
            } catch {
                // Malformed URL — fall through and treat as external
            }
            return { path: trimmed, isExternal: true };
        }

        // Relative URL explicitly flagged as external
        if (linkType === EXTERNAL_LINK_TYPE) {
            const extUrl = (trimmed.startsWith('http://') || trimmed.startsWith('https://')) ? trimmed : `https://${trimmed}`;
            return { path: extUrl, isExternal: true };
        }

        // Resolve based on content / target type if provided (prioritize targetId when set)
        const normalizedType = String(type || '').trim().toLowerCase();
        const normalizedLinkType = String(linkType || '').trim().toLowerCase();

        if (
            normalizedType === 'news article' ||
            normalizedType === 'news_posts' ||
            normalizedType === 'new management' ||
            normalizedType === 'news' ||
            normalizedLinkType === 'news'
        ) {
            const code = targetId || (trimmed.startsWith('/news/') ? trimmed.slice(6) : (trimmed !== '/news' && trimmed !== 'news' ? trimmed : ''));
            return { path: code ? `/news/${code}` : '/news', isExternal: false };
        }

        if (
            normalizedType === "speaker's profile" ||
            normalizedType === 'teachers' ||
            normalizedType === 'education management' ||
            normalizedType === 'education' ||
            normalizedType === 'teacher' ||
            normalizedType === 'speaker'
        ) {
            const code = targetId || (trimmed.startsWith('/online-class/') ? trimmed.slice(14) : (trimmed !== '/online-class' && trimmed !== 'online-class' ? trimmed : ''));
            return { path: code ? `/online-class/${code}` : '/online-class', isExternal: false };
        }

        if (
            normalizedType === 'course' ||
            normalizedType === 'courses' ||
            normalizedType === 'course management'
        ) {
            const code = targetId || (trimmed.startsWith('/online-class-member/') ? trimmed.slice(21) : (trimmed !== '/online-class-member' ? trimmed : ''));
            return { path: code ? `/online-class-member/${code}` : '/education', isExternal: false };
        }

        if (
            normalizedType === 'magazine' ||
            normalizedType === 'magazines' ||
            normalizedType === 'magazine management'
        ) {
            const code = targetId || (trimmed.startsWith('/magazine/') ? trimmed.slice(10) : (trimmed !== '/magazine' ? trimmed : ''));
            return { path: code ? `/magazine/${code}` : '/magazine', isExternal: false };
        }

        if (
            normalizedType === 'product details' ||
            normalizedType === 'products' ||
            normalizedType === 'product management' ||
            normalizedType === 'product'
        ) {
            const code = targetId || (trimmed.startsWith('/product/') ? trimmed.slice(9) : (trimmed !== '/product' ? trimmed : ''));
            return { path: code ? `/product/${code}` : '/product', isExternal: false };
        }

        if (
            normalizedType === 'product knowledge' ||
            normalizedType === 'productknowledges'
        ) {
            const code = targetId || (trimmed.startsWith('/product-knowledge/') ? trimmed.slice(19) : (trimmed !== '/product-knowledge' ? trimmed : ''));
            return { path: code ? `/product-knowledge/${code}` : '/product-knowledge', isExternal: false };
        }

        if (
            normalizedType === 'technique' ||
            normalizedType === 'technicals' ||
            normalizedType === 'technology display' ||
            normalizedType === 'technology' ||
            normalizedType === 'technical'
        ) {
            const code = targetId || (trimmed.startsWith('/technical/') ? trimmed.slice(11) : (trimmed !== '/technical' ? trimmed : ''));
            return { path: code ? `/technical/${code}` : '/technical', isExternal: false };
        }

        if (
            normalizedType === 'fashion' ||
            normalizedType === 'fashions' ||
            normalizedType === 'featured works'
        ) {
            const code = targetId || (trimmed.startsWith('/fashion/') ? trimmed.slice(9) : (trimmed !== '/fashion' ? trimmed : ''));
            return { path: code ? `/fashion/${code}` : '/fashion/detail', isExternal: false };
        }

        // Already a root-relative path (e.g. /online-class/68edd77422c8f0c6b1a31885)
        if (trimmed.startsWith('/')) {
            return { path: trimmed, isExternal: false };
        }

        // Bare internal-route segments (e.g. online-class/68edd77422c8f0c6b1a31885)
        if (INTERNAL_BARE_PREFIXES.some((prefix) => trimmed.startsWith(prefix))) {
            return { path: `/${trimmed}`, isExternal: false };
        }

        // Special prefix checks for IDs without route prefix
        if (trimmed.startsWith('PK') || trimmed.startsWith('pk')) {
            return { path: `/product-knowledge/${trimmed}`, isExternal: false };
        }
        if (trimmed.startsWith('TC') || trimmed.startsWith('tc')) {
            return { path: `/technical/${trimmed}`, isExternal: false };
        }
        if (trimmed.startsWith('NEWS') || trimmed.startsWith('news')) {
            return { path: `/news/${trimmed}`, isExternal: false };
        }

        // Default: treat as a product slug
        return { path: `${PRODUCT_BASE_PATH}${trimmed}`, isExternal: false };
    };

    const getImage = () => {
        const rawImgWidth = data?.imgwidth ?? data?.imgWidth ?? data?.width;
        let formattedWidth;
        if (rawImgWidth !== undefined && rawImgWidth !== null && rawImgWidth !== '') {
            if (typeof rawImgWidth === 'number' || (!isNaN(rawImgWidth) && !String(rawImgWidth).endsWith('%') && !String(rawImgWidth).endsWith('px') && !String(rawImgWidth).endsWith('rem') && !String(rawImgWidth).endsWith('vw'))) {
                formattedWidth = `${rawImgWidth}%`;
            } else {
                formattedWidth = String(rawImgWidth);
            }
        }

        const rawUrl = data?.linkUrl || data?.link;
        const { path: targetPath, isExternal } = resolveLink(
            rawUrl,
            data?.linkType,
            data?.type || data?.targetType,
            data?.targetId || data?.linkId
        );

        const handleClick = (e) => {
            if (!targetPath) return;
            if (isExternal) {
                // Handled natively by <a>
            } else {
                if (e) e.preventDefault();
                window.scrollTo(0, 0);
                navigate(targetPath);
            }
        };

        const imgElement = (
            <img
                className={`news-layout-image rounded-lg ${(targetPath || rawUrl) ? 'cursor-pointer hover:opacity-95 transition-opacity' : ''}`}
                style={{
                    ...style,
                    ...(data?.ratio ? { aspectRatio: `${data.ratio}` } : {}),
                    ...(formattedWidth ? { width: formattedWidth } : { width: '100%' }),
                    margin: "auto",
                    display: "block"
                }}
                src={data?.url}
                alt={data?.text || data?.url}
            />
        );

        if (targetPath || rawUrl) {
            return (
                <div style={{ width: '100%', display: 'flex', justifyContent: style?.textAlign === 'right' ? 'flex-end' : style?.textAlign === 'left' ? 'flex-start' : 'center' }}>
                    <a
                        href={targetPath || rawUrl}
                        target={isExternal ? '_blank' : '_self'}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                        onClick={handleClick}
                        style={{ display: 'inline-block', maxWidth: '100%', textDecoration: 'none' }}
                    >
                        {imgElement}
                    </a>
                </div>
            );
        }

        return (
            <div style={{ width: '100%', display: 'flex', justifyContent: style?.textAlign === 'right' ? 'flex-end' : style?.textAlign === 'left' ? 'flex-start' : 'center' }}>
                {imgElement}
            </div>
        );
    };

    const getVideo = () => {
        return <video 
            className='w-full' 
            key={data.url + data.type} 
            controls 
            controlsList="nodownload" 
            onContextMenu={(e) => e.preventDefault()} 
            style={{ width: "100%" }}
        >
            <source src={data.url} type={data.type} />
            Your browser does not support the video tag.
        </video>
    }

    const handleAccordin = () => {
        return <Collapse
            items={[{
                key: data?.index,
                label: (
                    <div className="flex items-center justify-between w-full pr-2">
                        <span>{data?.text}</span>
                        {/* {renderInlineAudio(data?.text)} */}
                    </div>
                ),
                children: data.accordion.map((item, idx) => <DynamicLayout key={idx} data={item} speech={speech} />),
            }]}
        />;
    }

    const getAds = () => {
        const rawImgWidth = data?.imgwidth ?? data?.imgWidth;
        let formattedWidth;
        if (rawImgWidth !== undefined && rawImgWidth !== null && rawImgWidth !== '') {
            if (typeof rawImgWidth === 'number' || (!isNaN(rawImgWidth) && !String(rawImgWidth).endsWith('%') && !String(rawImgWidth).endsWith('px') && !String(rawImgWidth).endsWith('rem') && !String(rawImgWidth).endsWith('vw'))) {
                formattedWidth = `${rawImgWidth}%`;
            } else {
                formattedWidth = String(rawImgWidth);
            }
        }

        const rawUrl = data?.linkUrl || data?.link;
        const { path: targetPath, isExternal } = resolveLink(
            rawUrl,
            data?.linkType,
            data?.type || data?.targetType,
            data?.targetId || data?.linkId
        );

        const handleClick = (e) => {
            if (!targetPath) return;
            if (isExternal) {
                // Handled natively by <a target="_blank">
            } else {
                if (e) e.preventDefault();
                window.scrollTo(0, 0);
                navigate(targetPath);
            }
        };

        const content = (
            <div
                className='relative inline-block overflow-hidden rounded-lg group'
                style={{
                    margin: "auto",
                    display: "block",
                    maxWidth: "100%",
                    ...formattedWidth ? { width: formattedWidth } : {},
                }}
            >
                <img
                    className='w-full rounded-lg news-layout-image cursor-pointer hover:opacity-95 transition-opacity'
                    style={{
                        ...style,
                        ...(data?.ratio ? { aspectRatio: `${data.ratio}` } : {}),
                        width: "100%",
                        margin: "auto",
                        display: "block",
                    }}
                    src={data?.url}
                    alt={data?.text || data?.title || data?.url || 'ad'}
                />
                {(targetPath || rawUrl) && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 group-hover:bg-black/75 text-white px-6 py-2 rounded-full text-sm font-medium z-10 flex items-center justify-center gap-1.5 transition-all shadow-md pointer-events-none whitespace-nowrap">
                        <span>{t('Click to go to the details page')}</span>
                        <span className="text-xs">&gt;</span>
                    </div>
                )}
            </div>
        );

        if (targetPath || rawUrl) {
            return (
                <a
                    href={targetPath || rawUrl}
                    target={isExternal ? '_blank' : '_self'}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    onClick={handleClick}
                    style={{ display: 'block', width: '100%', textDecoration: 'none' }}
                >
                    {content}
                </a>
            );
        }

        return content;
    };

    const handleType = (type) => {
        switch (type) {
            case "link":
                return getLink();
            case "button":
                return getButton();
            case "content":
                return getContent();
            case "h1":
                return getTitle(1);
            case "h2":
                return getTitle(2);
            case "h3":
                return getTitle(3);
            case "h4":
                return getTitle(4);
            case "h5":
                return getTitle(5);
            case "bullet":
                return getBullet();
            case "image":
                return getImage();
            case "video":
                return getVideo();
            case "accordion":
                return handleAccordin();
            case "ads":
            case "ad":
                return getAds();
            default:
                break;
        }
        return "";
    }

    return (
        <div key={data?.index}>
            {handleType(data?.tag)}
        </div>
    )
}
